/**
 * P2A.4 — Canonical persona workspace manifests. One entry per
 * {@link PersonaTag}. Drives shell persona switcher, persona-aware
 * default landing pages, and persona-default widget templates.
 *
 * Module IDs match `docs/module-catalog.json`.
 */
import type { PersonaTag, IndustryTag } from "./index";

export interface PersonaWorkspaceManifest {
  /** Stable identifier matching {@link PersonaTag}. */
  id: PersonaTag;
  /** Display name shown in shell switcher. */
  displayName: string;
  /** One-paragraph elevator pitch. */
  summary: string;
  /** Default landing route for this persona. */
  defaultLandingRoute: string;
  /** Module IDs surfaced in the persona's primary navigation. */
  primaryModules: string[];
  /** Module IDs surfaced as secondary / "more" navigation. */
  secondaryModules: string[];
  /** Widget template ID rendered on the persona dashboard. */
  defaultDashboardTemplate: string;
  /** Industries where this persona is most relevant. */
  industries: IndustryTag[];
  /** Permission scopes this persona normally requires. */
  defaultPermissionScopes: string[];
  /** Manifest schema version. */
  version: string;
}

export const PERSONA_WORKSPACES: PersonaWorkspaceManifest[] = [
  {
    id: "operator",
    displayName: "Operator",
    summary: "Shop-floor operator running jobs, logging events, completing checks.",
    defaultLandingRoute: "/workspace/operator",
    primaryModules: ["production", "alarms", "quality", "batch"],
    secondaryModules: ["materials", "equipment"],
    defaultDashboardTemplate: "persona.dashboard.operator",
    industries: [
      "medtech",
      "pharma-biotech",
      "food-beverage",
      "specialty-chemicals",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
    ],
    defaultPermissionScopes: ["production:execute", "quality:log", "alarms:ack"],
    version: "1.0.0",
  },
  {
    id: "supervisor",
    displayName: "Supervisor",
    summary: "Shift supervisor managing crews, escalations, and shift handover.",
    defaultLandingRoute: "/workspace/supervisor",
    primaryModules: ["production", "alarms", "quality", "maintenance"],
    secondaryModules: ["materials", "equipment", "planning"],
    defaultDashboardTemplate: "persona.dashboard.supervisor",
    industries: [
      "medtech",
      "pharma-biotech",
      "food-beverage",
      "specialty-chemicals",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["production:supervise", "quality:approve", "maintenance:assign"],
    version: "1.0.0",
  },
  {
    id: "controls-engineer",
    displayName: "Controls Engineer",
    summary: "PLC, OPC UA, SCADA tag and namespace owner.",
    defaultLandingRoute: "/workspace/controls-engineer",
    primaryModules: ["controls", "scada", "historian", "designer"],
    secondaryModules: ["alarms", "equipment"],
    defaultDashboardTemplate: "persona.dashboard.controls-engineer",
    industries: [
      "specialty-chemicals",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
      "pharma-biotech",
    ],
    defaultPermissionScopes: ["controls:edit", "scada:edit", "historian:read"],
    version: "1.0.0",
  },
  {
    id: "vision-engineer",
    displayName: "Vision Engineer",
    summary: "Camera, dataset, and inference model owner.",
    defaultLandingRoute: "/workspace/vision-engineer",
    primaryModules: ["vision", "quality", "equipment"],
    secondaryModules: ["alarms", "historian"],
    defaultDashboardTemplate: "persona.dashboard.vision-engineer",
    industries: ["medtech", "automotive-aerospace", "electronics-semiconductor"],
    defaultPermissionScopes: ["vision:edit", "vision:train", "quality:link"],
    version: "1.0.0",
  },
  {
    id: "maintenance-technician",
    displayName: "Maintenance Technician",
    summary: "Field tech executing PMs, repairs, and LOTO procedures.",
    defaultLandingRoute: "/workspace/maintenance-technician",
    primaryModules: ["maintenance", "equipment", "alarms"],
    secondaryModules: ["materials", "production"],
    defaultDashboardTemplate: "persona.dashboard.maintenance-technician",
    industries: [
      "food-beverage",
      "specialty-chemicals",
      "automotive-aerospace",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["maintenance:execute", "equipment:read", "alarms:ack"],
    version: "1.0.0",
  },
  {
    id: "quality-engineer",
    displayName: "Quality Engineer",
    summary: "Owns SPC, NCR/CAPA, inspections, and supplier quality.",
    defaultLandingRoute: "/workspace/quality-engineer",
    primaryModules: ["quality", "compliance", "vision", "batch"],
    secondaryModules: ["plm", "materials"],
    defaultDashboardTemplate: "persona.dashboard.quality-engineer",
    industries: [
      "medtech",
      "pharma-biotech",
      "food-beverage",
      "automotive-aerospace",
      "electronics-semiconductor",
    ],
    defaultPermissionScopes: ["quality:edit", "quality:disposition", "compliance:read"],
    version: "1.0.0",
  },
  {
    id: "compliance-officer",
    displayName: "Compliance Officer",
    summary: "Owns audit readiness, evidence packs, and validated state.",
    defaultLandingRoute: "/workspace/compliance-officer",
    primaryModules: ["compliance", "ebr", "admin", "quality"],
    secondaryModules: ["plm", "batch"],
    defaultDashboardTemplate: "persona.dashboard.compliance-officer",
    industries: [
      "medtech",
      "pharma-biotech",
      "food-beverage",
      "specialty-chemicals",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["compliance:edit", "audit:read", "evidence:export"],
    version: "1.0.0",
  },
  {
    id: "plm-engineer",
    displayName: "PLM Engineer",
    summary: "Owns BOMs, ECOs, recipes, and design history.",
    defaultLandingRoute: "/workspace/plm-engineer",
    primaryModules: ["plm", "designer", "batch"],
    secondaryModules: ["quality", "materials"],
    defaultDashboardTemplate: "persona.dashboard.plm-engineer",
    industries: [
      "medtech",
      "pharma-biotech",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
    ],
    defaultPermissionScopes: ["plm:edit", "plm:promote", "designer:read"],
    version: "1.0.0",
  },
  {
    id: "planner",
    displayName: "Planner",
    summary: "Owns demand, supply, capacity, and work order release.",
    defaultLandingRoute: "/workspace/planner",
    primaryModules: ["planning", "production", "materials"],
    secondaryModules: ["maintenance", "equipment"],
    defaultDashboardTemplate: "persona.dashboard.planner",
    industries: [
      "food-beverage",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["planning:edit", "production:release", "materials:read"],
    version: "1.0.0",
  },
  {
    id: "buyer",
    displayName: "Buyer",
    summary: "Owns POs, supplier qualification, and expedites.",
    defaultLandingRoute: "/workspace/buyer",
    primaryModules: ["procurement", "materials", "inventory"],
    secondaryModules: ["planning", "quality"],
    defaultDashboardTemplate: "persona.dashboard.buyer",
    industries: [
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["procurement:edit", "supplier:edit"],
    version: "1.0.0",
  },
  {
    id: "warehouse-lead",
    displayName: "Warehouse Lead",
    summary: "Owns receiving, putaway, picking, and cycle counts.",
    defaultLandingRoute: "/workspace/warehouse-lead",
    primaryModules: ["inventory", "materials"],
    secondaryModules: ["production", "procurement"],
    defaultDashboardTemplate: "persona.dashboard.warehouse-lead",
    industries: [
      "food-beverage",
      "automotive-aerospace",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["inventory:edit", "materials:move"],
    version: "1.0.0",
  },
  {
    id: "controller",
    displayName: "Controller",
    summary: "Owns financial close, journals, and cost accounting.",
    defaultLandingRoute: "/workspace/controller",
    primaryModules: ["finance", "costing"],
    secondaryModules: ["procurement", "production"],
    defaultDashboardTemplate: "persona.dashboard.controller",
    industries: ["multi-site-enterprise", "industrial-equipment-discrete"],
    defaultPermissionScopes: ["finance:edit", "costing:edit", "audit:read"],
    version: "1.0.0",
  },
  {
    id: "sales-ops",
    displayName: "Sales Ops",
    summary: "Owns CRM, quotes, sales orders, and credit holds.",
    defaultLandingRoute: "/workspace/sales-ops",
    primaryModules: ["crm", "sales"],
    secondaryModules: ["finance", "inventory"],
    defaultDashboardTemplate: "persona.dashboard.sales-ops",
    industries: ["multi-site-enterprise", "industrial-equipment-discrete"],
    defaultPermissionScopes: ["crm:edit", "sales:edit"],
    version: "1.0.0",
  },
  {
    id: "hr-training",
    displayName: "HR / Training",
    summary: "Owns workforce records, training, and certifications.",
    defaultLandingRoute: "/workspace/hr-training",
    primaryModules: ["hr", "compliance"],
    secondaryModules: ["admin"],
    defaultDashboardTemplate: "persona.dashboard.hr-training",
    industries: ["medtech", "pharma-biotech", "multi-site-enterprise"],
    defaultPermissionScopes: ["hr:edit", "training:edit"],
    version: "1.0.0",
  },
  {
    id: "sustainability-manager",
    displayName: "Sustainability Manager",
    summary: "Owns energy, emissions, water, and waste KPIs.",
    defaultLandingRoute: "/workspace/sustainability-manager",
    primaryModules: ["sustainability", "historian", "equipment"],
    secondaryModules: ["compliance", "production"],
    defaultDashboardTemplate: "persona.dashboard.sustainability-manager",
    industries: [
      "food-beverage",
      "specialty-chemicals",
      "automotive-aerospace",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["sustainability:edit", "historian:read"],
    version: "1.0.0",
  },
  {
    id: "integration-admin",
    displayName: "Integration Admin",
    summary: "Owns inbound/outbound connectors, message queues, and replays.",
    defaultLandingRoute: "/workspace/integration-admin",
    primaryModules: ["integration", "admin"],
    secondaryModules: ["historian", "alarms"],
    defaultDashboardTemplate: "persona.dashboard.integration-admin",
    industries: ["multi-site-enterprise", "pharma-biotech", "automotive-aerospace"],
    defaultPermissionScopes: ["integration:edit", "integration:replay", "admin:read"],
    version: "1.0.0",
  },
  {
    id: "executive",
    displayName: "Executive",
    summary: "Site / enterprise leader watching KPIs, exceptions, and trends.",
    defaultLandingRoute: "/workspace/executive",
    primaryModules: ["intelligence", "production", "quality", "finance"],
    secondaryModules: ["compliance", "sustainability"],
    defaultDashboardTemplate: "persona.dashboard.executive",
    industries: [
      "medtech",
      "pharma-biotech",
      "food-beverage",
      "specialty-chemicals",
      "automotive-aerospace",
      "electronics-semiconductor",
      "industrial-equipment-discrete",
      "multi-site-enterprise",
    ],
    defaultPermissionScopes: ["dashboard:read", "intelligence:read"],
    version: "1.0.0",
  },
  {
    id: "developer-validation",
    displayName: "Developer / Validation Engineer",
    summary: "Owns automated tests, validation evidence, and CSV/CSA artifacts.",
    defaultLandingRoute: "/workspace/developer-validation",
    primaryModules: ["compliance", "ebr", "admin"],
    secondaryModules: ["integration", "quality"],
    defaultDashboardTemplate: "persona.dashboard.developer-validation",
    industries: ["medtech", "pharma-biotech"],
    defaultPermissionScopes: ["validation:edit", "evidence:export", "admin:read"],
    version: "1.0.0",
  },
];

export function getPersonaWorkspace(id: string): PersonaWorkspaceManifest | undefined {
  return PERSONA_WORKSPACES.find((p) => p.id === id);
}
